import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth-context';
import { employeeAPI, branchAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Users, Search, KeyRound, ShieldCheck, Trash2, Link2, Copy, RotateCcw, UserMinus } from 'lucide-react';

export default function EmployeesPage() {
  const { isSuperUser, isAdmin } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteMode, setDeleteMode] = useState('deactivate'); // 'deactivate' or 'permanent'
  const [offboardOpen, setOffboardOpen] = useState(false);
  const [offboardTarget, setOffboardTarget] = useState(null);
  const [offboardForm, setOffboardForm] = useState({ exit_date: '', exit_reason: '' });
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [filterBranch, setFilterBranch] = useState('all');
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({
    email: '', name: '', phone: '', role: 'staff',
    staff_role: '', branch_id: '', joining_date: '', salary: 0,
    salary_type: 'monthly', employment_type: 'permanent',
    is_permanent: false, leave_entitlement_months: 3,
    auth_type: 'google', password: '', permissions: []
  });

  useEffect(() => {
    Promise.all([loadEmployees(), loadBranches()]);
  }, []);

  const loadEmployees = async () => {
    try {
      const res = await employeeAPI.list();
      setEmployees(res.data);
    } catch { toast.error('Failed to load employees'); }
    finally { setLoading(false); }
  };

  const loadBranches = async () => {
    try {
      const res = await branchAPI.list();
      setBranches(res.data);
    } catch (e) { console.error(e); }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.email) { toast.error('Name and email are required'); return; }
    if (!editEmp && form.auth_type === 'password' && (!form.password || form.password.length < 6)) {
      toast.error('Password must be at least 6 characters for local auth users');
      return;
    }
    try {
      if (editEmp) {
        const { password, ...updateData } = form;
        // Only include email if super user changed it
        if (updateData.email === editEmp.email) {
          delete updateData.email;
        }
        await employeeAPI.update(editEmp.id, updateData);
        toast.success('Employee updated');
      } else {
        if (!form.branch_id) { toast.error('Branch is required'); return; }
        await employeeAPI.create(form);
        toast.success('Employee created');
      }
      setDialogOpen(false);
      setEditEmp(null);
      loadEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (emp) => {
    setEditEmp(emp);
    setForm({
      email: emp.email, name: emp.name, phone: emp.phone || '',
      role: emp.role, staff_role: emp.staff_role || '', branch_id: emp.branch_id || '',
      joining_date: emp.joining_date || '', salary: emp.salary || 0,
      salary_type: emp.salary_type || 'monthly',
      employment_type: emp.employment_type || 'permanent',
      is_permanent: emp.is_permanent || false, leave_entitlement_months: emp.leave_entitlement_months || 3,
      auth_type: emp.auth_type || 'google', password: '', permissions: emp.permissions || []
    });
    setDialogOpen(true);
  };

  const openNew = () => {
    setEditEmp(null);
    setForm({
      email: '', name: '', phone: '', role: 'staff',
      staff_role: '', branch_id: branches[0]?.id || '', joining_date: new Date().toISOString().split('T')[0],
      salary: 0, salary_type: 'monthly', employment_type: 'permanent',
      is_permanent: false, leave_entitlement_months: 3,
      auth_type: 'google', password: '', permissions: []
    });
    setDialogOpen(true);
  };

  const openSetPassword = (emp) => {
    setPasswordTarget(emp);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const handleSetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await employeeAPI.setPassword(passwordTarget.id, newPassword);
      toast.success(`Password set for ${passwordTarget.name}`);
      setPasswordDialogOpen(false);
      loadEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set password');
    }
  };

  const openDeleteDialog = (emp) => {
    setDeleteTarget(emp);
    setDeleteMode('deactivate');
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      if (deleteMode === 'permanent') {
        await employeeAPI.permanentDelete(deleteTarget.id);
        toast.success(`${deleteTarget.name} permanently deleted`);
      } else {
        await employeeAPI.delete(deleteTarget.id);
        toast.success(`${deleteTarget.name} has been deactivated`);
      }
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      loadEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete employee');
    }
  };

  const handleActivate = async (emp) => {
    try {
      await employeeAPI.update(emp.id, { is_active: true });
      toast.success(`${emp.name} has been reactivated`);
      loadEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reactivate employee');
    }
  };

  const openOffboard = (emp) => {
    setOffboardTarget(emp);
    setOffboardForm({ exit_date: new Date().toISOString().split('T')[0], exit_reason: '' });
    setOffboardOpen(true);
  };

  const handleOffboard = async () => {
    if (!offboardForm.exit_date) { toast.error('Exit date is required'); return; }
    if (!offboardForm.exit_reason.trim()) { toast.error('Exit reason is required'); return; }
    try {
      await employeeAPI.offboard(offboardTarget.id, offboardForm);
      toast.success(`${offboardTarget.name} has been offboarded`);
      setOffboardOpen(false);
      setOffboardTarget(null);
      loadEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to offboard employee');
    }
  };

  const copyWelcomeLink = (emp) => {
    const baseUrl = window.location.origin;
    const welcomeUrl = `${baseUrl}/welcome?name=${encodeURIComponent(emp.name)}&email=${encodeURIComponent(emp.email)}&auth=${encodeURIComponent(emp.auth_type || 'google')}`;
    navigator.clipboard.writeText(welcomeUrl).then(() => {
      toast.success(`Welcome link copied for ${emp.name}`);
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  const filtered = employees.filter(emp => {
    if (filterBranch !== 'all' && emp.branch_id !== filterBranch) return false;
    if (search && !emp.name.toLowerCase().includes(search.toLowerCase()) && !emp.email.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const getBranchName = (branchId) => branches.find(b => b.id === branchId)?.name || '-';

  const roleColors = { manager: 'default', staff: 'secondary' };
  const staffRoleLabels = { sales: 'Sales', marketing: 'Marketing', case_manager: 'Case Manager', manager: 'Manager' };
  const empTypeLabels = { permanent: 'Permanent', probation: 'Probation', contractor: 'Contractor' };
  const empTypeColors = { permanent: 'default', probation: 'outline', contractor: 'secondary' };

  const getCurrencySymbol = (branchId) => {
    const branch = branches.find(b => b.id === branchId);
    const currency = branch?.currency || 'CAD';
    const symbols = { INR: '₹', CAD: 'C$', USD: '$', GBP: '£', EUR: '€' };
    return symbols[currency] || currency;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold tracking-tight">Employees</h1>
          <p className="text-muted-foreground">Manage team members across branches</p>
        </div>
        {isAdmin && (
          <Button onClick={openNew} className="gap-2" data-testid="create-employee-button">
            <Plus size={16} /> Add Employee
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="employee-search-input" />
        </div>
        {isSuperUser && branches.length > 0 && (
          <Select value={filterBranch} onValueChange={setFilterBranch}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by branch" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Users className="mx-auto mb-3 text-muted-foreground/50" size={40} />
              <p>No employees found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Branch</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Auth</TableHead>
                    <TableHead>Joining Date</TableHead>
                    <TableHead className="text-right">Salary</TableHead>
                    <TableHead>Status</TableHead>
                    {isAdmin && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(emp => (
                    <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`}>
                      <TableCell className="font-medium">{emp.name}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{emp.email}</TableCell>
                      <TableCell>{getBranchName(emp.branch_id)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Badge variant={roleColors[emp.role] || 'outline'} className="capitalize text-xs">{emp.role}</Badge>
                          {emp.staff_role && <Badge variant="outline" className="capitalize text-xs">{staffRoleLabels[emp.staff_role] || emp.staff_role}</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={empTypeColors[emp.employment_type] || 'outline'} className="capitalize text-xs">
                          {empTypeLabels[emp.employment_type] || emp.employment_type || 'Permanent'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs gap-1">
                          {emp.auth_type === 'password' ? (
                            <><KeyRound size={10} /> Local</>
                          ) : (
                            <><ShieldCheck size={10} /> Google</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="tabular-nums">{emp.joining_date}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {getCurrencySymbol(emp.branch_id)}{emp.salary?.toLocaleString()}
                        <span className="text-muted-foreground text-xs ml-1">/{emp.salary_type === 'yearly' ? 'yr' : 'mo'}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={emp.is_active ? 'default' : 'destructive'} className="text-xs">
                          {emp.is_active ? 'Active' : (emp.exit_date ? 'Offboarded' : 'Inactive')}
                        </Badge>
                        {!emp.is_active && emp.exit_date && (
                          <p className="text-[11px] text-muted-foreground mt-1" title={emp.exit_reason}>
                            Left {emp.exit_date}{emp.exit_reason ? ` — ${emp.exit_reason}` : ''}
                          </p>
                        )}
                      </TableCell>
                      {isAdmin && (
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(emp)} data-testid={`edit-employee-${emp.id}`}>
                              <Pencil size={14} />
                            </Button>
                            {(emp.auth_type === 'password' || !emp.auth_type) && (
                              <Button variant="ghost" size="sm" onClick={() => openSetPassword(emp)} title="Set Password" data-testid={`set-password-${emp.id}`}>
                                <KeyRound size={14} />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => copyWelcomeLink(emp)} title="Copy Welcome Link" data-testid={`welcome-link-${emp.id}`}>
                              <Link2 size={14} className="text-primary" />
                            </Button>
                            {emp.is_active ? (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => openOffboard(emp)} title="Offboard (employee left)" data-testid={`offboard-employee-${emp.id}`}>
                                  <UserMinus size={14} className="text-amber-600" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => openDeleteDialog(emp)} title="Deactivate / Delete" data-testid={`delete-employee-${emp.id}`}>
                                  <Trash2 size={14} className="text-destructive" />
                                </Button>
                              </>
                            ) : (
                              <Button variant="ghost" size="sm" onClick={() => handleActivate(emp)} title="Reactivate" data-testid={`activate-employee-${emp.id}`}>
                                <RotateCcw size={14} className="text-emerald-600" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Employee Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editEmp ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="employee-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email {editEmp && isSuperUser && <span className="text-xs text-muted-foreground">(editable — this is the login email)</span>}</Label>
              <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} disabled={editEmp && !isSuperUser} placeholder="employee@visaxpress.ca" data-testid="employee-email-input" />
              {editEmp && isSuperUser && form.email !== editEmp.email && (
                <p className="text-xs text-amber-600">Changing email will update this employee's login credentials</p>
              )}
            </div>

            {/* Auth Method */}
            <div className="space-y-2">
              <Label>Authentication Method</Label>
              <Select value={form.auth_type} onValueChange={v => setForm({...form, auth_type: v, password: ''})}>
                <SelectTrigger data-testid="employee-auth-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="google">
                    <div className="flex items-center gap-2">
                      <ShieldCheck size={14} /> Google SSO (Company Email)
                    </div>
                  </SelectItem>
                  <SelectItem value="password">
                    <div className="flex items-center gap-2">
                      <KeyRound size={14} /> Email & Password (Local)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {form.auth_type === 'google'
                  ? 'Employee will sign in using their Google account'
                  : 'Employee will sign in with email and a password you set'}
              </p>
            </div>

            {/* Password field - only for local auth on new employees */}
            {!editEmp && form.auth_type === 'password' && (
              <div className="space-y-2">
                <Label>Initial Password</Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  placeholder="Min. 6 characters"
                  data-testid="employee-password-input"
                />
                <p className="text-xs text-muted-foreground">
                  Share this password with the employee securely. They can use it to log in.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                  <SelectTrigger data-testid="employee-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Staff Role</Label>
                <Select value={form.staff_role || 'none'} onValueChange={v => setForm({...form, staff_role: v === 'none' ? '' : v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="sales">Sales</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="case_manager">Case Manager</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Branch</Label>
                <Select value={form.branch_id} onValueChange={v => setForm({...form, branch_id: v})}>
                  <SelectTrigger data-testid="employee-branch-select">
                    <SelectValue placeholder="Select branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Joining Date</Label>
                <Input type="date" value={form.joining_date} onChange={e => setForm({...form, joining_date: e.target.value})} data-testid="employee-joining-date-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Monthly Salary</Label>
                <Input type="number" value={form.salary} onChange={e => setForm({...form, salary: parseFloat(e.target.value) || 0})} data-testid="employee-salary-input" />
              </div>
              <div className="space-y-2">
                <Label>Salary Frequency</Label>
                <Select value={form.salary_type} onValueChange={v => setForm({...form, salary_type: v})}>
                  <SelectTrigger data-testid="employee-salary-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Per Month</SelectItem>
                    <SelectItem value="yearly">Per Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Employment Type</Label>
                <Select value={form.employment_type} onValueChange={v => setForm({...form, employment_type: v})}>
                  <SelectTrigger data-testid="employee-employment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="permanent">Permanent</SelectItem>
                    <SelectItem value="probation">Probation</SelectItem>
                    <SelectItem value="contractor">Contractor (No Check-in)</SelectItem>
                  </SelectContent>
                </Select>
                {form.employment_type === 'contractor' && (
                  <p className="text-xs text-muted-foreground">Contractors are not required to check in/out and won't be marked absent</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Leave Entitlement (months after joining)</Label>
                <Input type="number" value={form.leave_entitlement_months} onChange={e => setForm({...form, leave_entitlement_months: parseInt(e.target.value) || 0})} />
                <p className="text-xs text-muted-foreground">Leaves available after {form.leave_entitlement_months} months</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_permanent} onCheckedChange={v => setForm({...form, is_permanent: v})} />
              <Label>Permanent Employee</Label>
            </div>
            <div className="flex items-center gap-3 rounded-lg border p-3 bg-muted/30">
              <Switch
                checked={(form.permissions || []).includes('lead_management')}
                onCheckedChange={v => setForm({
                  ...form,
                  permissions: v
                    ? Array.from(new Set([...(form.permissions || []), 'lead_management']))
                    : (form.permissions || []).filter(p => p !== 'lead_management')
                })}
                data-testid="lead-access-switch"
              />
              <div>
                <Label>Lead Management access</Label>
                <p className="text-xs text-muted-foreground">Can view & work leads. Sales reps, managers & super admin already have access.</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="employee-save-button">{editEmp ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Set Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Set a new password for <strong>{passwordTarget?.name}</strong> ({passwordTarget?.email})
            </p>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Min. 6 characters"
                data-testid="set-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSetPassword} data-testid="set-password-confirm-button">Set Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{deleteMode === 'permanent' ? 'Permanently Delete Employee' : 'Deactivate Employee'}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  {deleteMode === 'permanent'
                    ? <>This will <strong className="text-destructive">permanently delete</strong> <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email}) and all their attendance, leave, and salary records. This cannot be undone.</>
                    : <>Are you sure you want to deactivate <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email})? They will no longer be able to log in. This can be reversed by editing the employee.</>
                  }
                </p>
                {isSuperUser && (
                  <div className="flex items-center gap-3 pt-2 border-t">
                    <Switch
                      checked={deleteMode === 'permanent'}
                      onCheckedChange={(v) => setDeleteMode(v ? 'permanent' : 'deactivate')}
                      data-testid="permanent-delete-toggle"
                    />
                    <Label className="text-sm font-medium text-destructive cursor-pointer">
                      Permanently delete (irreversible)
                    </Label>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-employee-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-employee-confirm"
            >
              {deleteMode === 'permanent' ? 'Delete Permanently' : 'Deactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Offboard Dialog */}
      <Dialog open={offboardOpen} onOpenChange={setOffboardOpen}>
        <DialogContent className="max-w-md" data-testid="offboard-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Offboard Employee</DialogTitle>
            <DialogDescription>
              Record that <strong>{offboardTarget?.name}</strong> has left the company. This deactivates their access; you can reactivate later if needed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Exit date</Label>
              <Input type="date" value={offboardForm.exit_date} onChange={e => setOffboardForm({ ...offboardForm, exit_date: e.target.value })} data-testid="offboard-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Reason for leaving</Label>
              <Input value={offboardForm.exit_reason} onChange={e => setOffboardForm({ ...offboardForm, exit_reason: e.target.value })} placeholder="e.g. Resigned, End of contract, Terminated" data-testid="offboard-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOffboardOpen(false)}>Cancel</Button>
            <Button onClick={handleOffboard} data-testid="offboard-confirm-button">Offboard</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
